import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Shield, ShieldCheck, ShieldAlert, Eye, MoreVertical, Plus, Edit } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, User as UserType } from '../../lib/api/users';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { UserEditModal } from '../../components/users/UserEditModal';
import { UserFormData } from '../../components/users/UserForm';
import { toast } from '../../stores/toastStore';

const ROLE_STYLES: Record<string, { label: string; className: string; Icon: React.ElementType }> = {
  superuser: { label: 'Superuser', className: 'bg-purple-100 text-purple-700', Icon: ShieldAlert },
  admin:     { label: 'Admin',     className: 'bg-blue-100 text-blue-700',     Icon: ShieldCheck },
  deployer:  { label: 'Deployer',  className: 'bg-green-100 text-green-700',   Icon: Shield },
  viewer:    { label: 'Viewer',    className: 'bg-gray-100 text-gray-600',      Icon: Eye },
};

export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);
  const [userToEdit, setUserToEdit] = useState<UserType | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search],
    queryFn: () => usersApi.list({ page, limit: 20, search: search || undefined }),
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setUserToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete user');
    },
  });

  const updateUser = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserFormData }) => usersApi.update(id, data),
    onSuccess: () => {
      toast.success('User updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setUserToEdit(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update user');
    },
  });

  const handleDelete = (user: UserType) => {
    setUserToDelete(user);
  };

  const handleEdit = (user: UserType) => {
    setUserToEdit(user);
  };

  const handleUpdate = async (data: UserFormData) => {
    if (userToEdit) {
      await updateUser.mutateAsync({ id: userToEdit.id, data });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600">Manage team members and permissions</p>
        </div>
        <Button onClick={() => navigate('/users/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="animate-pulse flex items-center gap-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : data?.items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500">Contact your administrator to add team members.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {data?.items.map((user: UserType) => (
              <Card key={user.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-full bg-primary-100">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-gray-900">{user.name || user.email}</h3>
                        {(() => {
                          const style = ROLE_STYLES[user.role] ?? ROLE_STYLES.viewer;
                          return (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${style.className}`}>
                              {style.label}
                            </span>
                          );
                        })()}
                        {!user.is_active && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const style = ROLE_STYLES[user.role] ?? ROLE_STYLES.viewer;
                        const Icon = style.Icon;
                        return <Icon className={`h-5 w-5 ${user.role === 'superuser' ? 'text-purple-500' : user.role === 'admin' ? 'text-blue-500' : user.role === 'deployer' ? 'text-green-500' : 'text-gray-300'}`} />;
                      })()}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        disabled={updateUser.isPending}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user)}
                        disabled={deleteUser.isPending}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {data && data.pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="flex items-center px-3 text-sm text-gray-600">
                Page {page} of {data.pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= data.pagination.total_pages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Edit User Modal */}
      {userToEdit && (
        <UserEditModal
          user={userToEdit}
          isOpen={!!userToEdit}
          onClose={() => setUserToEdit(null)}
          onUpdate={handleUpdate}
          isLoading={updateUser.isPending}
        />
      )}

      {/* Delete User Confirmation Modal */}
      <ConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={() => userToDelete && deleteUser.mutate(userToDelete.id)}
        title="Delete User"
        message={
          <>
            Are you sure you want to delete <strong>{userToDelete?.name || userToDelete?.email}</strong>?
            This action cannot be undone.
          </>
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleteUser.isPending}
      />
    </div>
  );
}
